import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, User, Search, ChevronRight, AlertTriangle, FileText, Clock,
  Phone, Building2, UserCheck, CheckCircle, XCircle, PauseCircle, Mail,
  GraduationCap, RotateCcw, LogOut, Archive, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import UserAvatar from '../components/UserAvatar';
import { DossiersLotBar, SelectCheckbox } from '../components/DossiersLotBar';
import { MoniteursLotDialog } from '../components/MoniteursLotDialog';

const ListeStagiaires = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCentre, setFilterCentre] = useState('all');
  const [filterAnnee, setFilterAnnee] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [loading, setLoading] = useState(true);
  const [downloadingTrombi, setDownloadingTrombi] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showMoniteursLot, setShowMoniteursLot] = useState(false);
  const canBulk = ['centre', 'responsable_pedagogique', 'direction'].includes(user?.role);
  const toggleSelected = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const navigate = useNavigate();

  // Générer les années disponibles (de 2020 à année courante + 1)
  const currentYear = new Date().getFullYear();
  const annees = [];
  for (let y = currentYear + 1; y >= 2020; y--) {
    annees.push(y);
  }

  useEffect(() => {
    const controller = new AbortController();
    loadStagiaires(controller.signal);
    return () => controller.abort();
  }, []);

  const loadStagiaires = async (signal) => {
    try {
      // ENDPOINT AGRÉGÉ: 1 requête au lieu de 33 (1 + 16*2)
      const response = await axios.get('/stagiaires-enriched', { signal });
      setStagiaires(response.data || []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredStagiaires = stagiaires.filter(stag => {
    const matchSearch = `${stag.user_info?.prenom} ${stag.user_info?.nom}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCentre = filterCentre === 'all' || stag.centre_formation === filterCentre;
    const notDeleted = stag.statut_validation !== 'supprime';
    
    // Filtre par statut formation (diplômé, redoublant, abandon, archivé)
    let matchStatut = true;
    if (filterStatut === 'archives') {
      matchStatut = stag.archived === true;
    } else if (filterStatut !== 'all') {
      matchStatut = stag.statut_formation === filterStatut && !stag.archived;
    } else {
      matchStatut = !stag.archived;
    }
    
    // Filtre par année (basé sur la date de création ou validated_at)
    let matchAnnee = true;
    if (filterAnnee !== 'all') {
      const dateField = stag.validated_at || stag.created_at || stag.date_inscription;
      if (dateField) {
        const anneeStag = new Date(dateField).getFullYear();
        matchAnnee = anneeStag === parseInt(filterAnnee);
      } else {
        matchAnnee = false;
      }
    }
    
    return matchSearch && matchCentre && matchAnnee && matchStatut && notDeleted;
  });

  const handleDownloadTrombinoscope = async () => {
    setDownloadingTrombi(true);
    try {
      toast.info('Generation du trombinoscope en cours...');
      const response = await axios.get('/stagiaires-trombinoscope/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${new Date().getFullYear()}_trombinoscope_stagiaires.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Trombinoscope telecharge !');
    } catch (error) {
      toast.error('Erreur lors de la generation du trombinoscope');
    }
    setDownloadingTrombi(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="liste-stagiaires">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Stagiaires</h1>
          <p className="text-slate-600">Liste complete avec alertes et informations detaillees</p>
        </div>
        <Button
          onClick={handleDownloadTrombinoscope}
          variant="outline"
          className="border-[#8DC63F] text-[#8DC63F] hover:bg-green-50"
          disabled={downloadingTrombi}
          data-testid="btn-trombinoscope-stagiaires"
        >
          <Camera className="h-4 w-4 mr-2" />
          {downloadingTrombi ? 'Generation...' : 'Trombinoscope PDF'}
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un stagiaire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="search-input"
          />
        </div>
        <select
          value={filterCentre}
          onChange={(e) => setFilterCentre(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          data-testid="filter-centre"
        >
          <option value="all">Tous les centres</option>
          <option value="Atlantique">Atlantique</option>
          <option value="Occitanie">Occitanie</option>
        </select>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          data-testid="filter-statut"
        >
          <option value="all">Tous les statuts</option>
          <option value="en_formation">En formation</option>
          <option value="diplome">Diplômé(e)</option>
          <option value="redoublant">Redoublant</option>
          <option value="abandon">Abandon</option>
          <option value="archives">Archivés (Moniteurs)</option>
        </select>
        <select
          value={filterAnnee}
          onChange={(e) => setFilterAnnee(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          data-testid="filter-annee"
        >
          <option value="all">Toutes les années</option>
          {annees.map(annee => (
            <option key={annee} value={annee}>{annee}</option>
          ))}
        </select>
      </div>

      {/* Liste */}
      {canBulk && filteredStagiaires.length > 0 && (() => {
        const ids = filteredStagiaires.map(s => s.user_id);
        const allChecked = ids.every(id => selected.includes(id));
        return (
          <button
            type="button"
            onClick={() => setSelected(allChecked ? selected.filter(id => !ids.includes(id)) : Array.from(new Set([...selected, ...ids])))}
            className="text-sm font-medium text-[#0F172A] hover:text-[#8DC63F] underline underline-offset-4"
            data-testid="select-all-stagiaires"
          >
            {allChecked ? 'Tout décocher' : `Tout cocher (${ids.length})`}
          </button>
        );
      })()}
      {canBulk && (
        <DossiersLotBar
          source="stagiaires"
          selectedIds={selected}
          onClear={() => setSelected([])}
          extraActions={
            <Button onClick={() => setShowMoniteursLot(true)} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="moniteurs-lot-open-btn">
              <GraduationCap className="h-4 w-4 mr-2" /> Passer en Moniteur VNM
            </Button>
          }
        />
      )}
      {canBulk && (
        <MoniteursLotDialog
          open={showMoniteursLot}
          onOpenChange={setShowMoniteursLot}
          selectedIds={selected}
          onDone={() => { setSelected([]); loadStagiaires(); }}
        />
      )}
      <div className="space-y-3">
        {filteredStagiaires.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            {filterStatut === 'archives' ? (
              <>
                <Archive className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun stagiaire archivé</p>
                <p className="text-sm text-slate-400 mt-1">Les stagiaires convertis en Moniteur VNM apparaîtront ici</p>
              </>
            ) : (
              <>
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun stagiaire trouvé</p>
              </>
            )}
          </div>
        ) : (
          filteredStagiaires.map((stag) => (
            <Link
              key={stag.user_id}
              to={`/fiche-stagiaire?id=${stag.user_id}`}
              className="block bg-white border border-slate-200 rounded-lg p-4 hover:border-[#8DC63F] hover:shadow-md transition-all"
              data-testid={`stagiaire-${stag.user_id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  {canBulk && (
                    <SelectCheckbox checked={selected.includes(stag.user_id)} onToggle={() => toggleSelected(stag.user_id)} testId={`select-stagiaire-${stag.user_id}`} />
                  )}
                  {/* Photo ou initiales */}
                  <UserAvatar user={stag.user_info || stag} size="md" />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-[#0F172A]">
                        {stag.user_info?.prenom} {stag.user_info?.nom}
                      </h3>
                      {stag.user_info?.numero_candidat && (
                        <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200" data-testid={`numero-candidat-${stag.user_id}`}>
                          N°{stag.user_info.numero_candidat}
                        </span>
                      )}
                      {stag.hasDeclaration && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full" data-testid={`badge-declare-${stag.user_id}`}>
                          <CheckCircle className="h-3 w-3" /> Stagiaire déclaré
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{stag.centre_formation}</p>
                  </div>
                </div>
                
                {/* Statut formation - Badge bien visible à droite */}
                <div className="flex flex-wrap items-center gap-3">
                  {stag.archived && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white text-sm font-medium rounded-lg shadow-sm">
                      <Archive className="h-4 w-4" /> Moniteur VNM
                    </span>
                  )}
                  {stag.statut_formation === 'diplome' && !stag.archived && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-sm font-medium rounded-lg shadow-sm">
                      <GraduationCap className="h-4 w-4" /> Diplômé(e)
                    </span>
                  )}
                  {stag.statut_formation === 'redoublant' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-lg shadow-sm">
                      <RotateCcw className="h-4 w-4" /> Redoublant
                    </span>
                  )}
                  {stag.statut_formation === 'abandon' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg shadow-sm">
                      <LogOut className="h-4 w-4" /> Abandon
                    </span>
                  )}
                  {(!stag.statut_formation || stag.statut_formation === 'en_formation') && stag.statut_validation === 'accepte' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#8DC63F] text-white text-sm font-medium rounded-lg shadow-sm">
                      <CheckCircle className="h-4 w-4" /> En formation
                    </span>
                  )}
                  {stag.statut_validation === 'en_attente' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-sm font-medium rounded-lg shadow-sm">
                      <Clock className="h-4 w-4" /> En attente
                    </span>
                  )}
                  {stag.statut_validation === 'veille' && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-500 text-white text-sm font-medium rounded-lg shadow-sm">
                      <PauseCircle className="h-4 w-4" /> En veille
                    </span>
                  )}
                  
                  {/* Alertes */}
                  <div className="flex flex-wrap gap-1">
                    {stag.missingDocs?.length > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                        <FileText className="h-3 w-3" /> {stag.missingDocs.length} doc
                      </span>
                    )}
                    {stag.absences > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                        <XCircle className="h-3 w-3" /> {stag.absences} abs
                      </span>
                    )}
                    {stag.retards > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                        <Clock className="h-3 w-3" /> {stag.retards} ret
                      </span>
                    )}
                    {stag.justifies > 0 && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                        <FileText className="h-3 w-3" /> {stag.justifies} just
                      </span>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default ListeStagiaires;
